/**
 * One place that builds request headers, and one way to force a real refetch.
 *
 * WHY THIS EXISTS
 * Ten separate copies of authHeaders() had grown across pages and services.
 * They agreed on auth and tenant, so nothing looked wrong - but it meant any
 * cross-cutting header change had to be made ten times, and in practice never
 * was. The Refresh button is exactly that kind of change: cost responses are
 * cached server-side for an hour, so a refresh that does not say "skip the
 * cache" re-renders the identical payload and looks broken.
 *
 * THE REFRESH MECHANISM
 * withFreshData() marks a scope as a deliberate user refresh; every
 * authHeaders() call inside it adds `Cache-Control: no-cache`, which the API
 * honours by skipping its read cache (see cache.service's shouldBypassCache).
 * A page therefore refreshes properly by wrapping its existing fetch:
 *
 *     await withFreshData(() => fetchData());
 *
 * - with no change to the signature of anything in between. That matters
 * because the alternative, threading a forceFresh boolean down through every
 * service call, is what stopped this being fixed everywhere the first time.
 *
 * The depth counter is deliberately module-global rather than per-request. A
 * fetch started outside the scope but still in flight when a refresh begins
 * could pick up the header; the cost of that is one extra cache miss, which is
 * a far better failure than a refresh button that silently does nothing.
 */
import { getActiveTenantId } from './tenantContext';

let freshDepth = 0;

/** True while a user-initiated refresh is in progress. */
export function isForcingFresh(): boolean {
  return freshDepth > 0;
}

/**
 * Run `fn` as a deliberate refresh: everything it fetches bypasses the server
 * read cache. Always restores the previous state, including on throw.
 */
export async function withFreshData<T>(fn: () => Promise<T>): Promise<T> {
  freshDepth++;
  try {
    return await fn();
  } finally {
    freshDepth--;
  }
}

/**
 * Standard headers for every API call.
 *
 * The token is read fresh on each call rather than captured once, because
 * tenantContextMiddleware requires a verified Bearer token and a captured one
 * goes stale after a silent refresh.
 */
export function authHeaders(forceFresh?: boolean): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  const fresh = forceFresh ?? isForcingFresh();
  return {
    'x-tenant-id': getActiveTenantId(),
    'x-user-id': 'user-123',
    ...(fresh ? { 'Cache-Control': 'no-cache' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}
